import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { BetslipStore } from "./betslipTypes";
import {
  betslipCategories,
  betslipInitialStakesMap,
  betslipInitState,
} from "./betslipInitState";
import {
  addSelection,
  deleteEventSelections,
  deleteSelection,
  hasMaxSelectionsCount,
  onSelectionAdd,
  onSelectionDelete,
} from "./betslipHelpers";

const storageName = "unstable_betslip_version_7";
const INACTIVITY_TIMEOUT = 15 * 60 * 1000;

let inactivityTimer: NodeJS.Timeout | null = null;

const checkAndClearIfExpired = (
  lastActivityTime: number | null,
  clearFn: () => void
) => {
  if (!lastActivityTime) return;

  const now = Date.now();
  const timeSinceLastActivity = now - lastActivityTime;

  if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
    clearFn();
  }
};

const startInactivityTimer = (clearFn: () => void) => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  inactivityTimer = setTimeout(() => {
    clearFn();
  }, INACTIVITY_TIMEOUT);
};

const updateLastActivity = (set: any, get: any) => {
  set((state: any) => {
    state.lastActivityTime = Date.now();
  });
  startInactivityTimer(() => get().clear());
};

const withImmer = immer<BetslipStore>((set, get) => ({
  ...betslipInitState,
  lastActivityTime: null,

  setIsLocked(isLocked) {
    set({ isLocked });
    updateLastActivity(set, get);
  },

  setCategory(category) {
    set((state) => {
      if (state.isLocked) return;

      if (betslipCategories.includes(category)) {
        state.category = category;
      }
    });
    updateLastActivity(set, get);
  },

  setSystemComboStake(value, comboKeys) {
    set((state) => {
      if (state.isLocked) return;

      state.systemComboStake = value;

      comboKeys.forEach((key) => {
        state.stakesMap.system[key] = value;
      });
    });
    updateLastActivity(set, get);
  },

  setStake(payload) {
    set((state) => {
      if (state.isLocked) return;

      const stakes = state.stakesMap[payload.category];
      if (stakes) stakes[payload.key] = payload.value;
    });
    updateLastActivity(set, get);
  },

  deleteStake(payload) {
    set((state) => {
      if (state.isLocked) return;

      const stakes = state.stakesMap[payload.category];
      if (stakes) delete stakes[payload.key];
    });
    updateLastActivity(set, get);
  },

  clearStakes(category) {
    set((state) => {
      if (state.isLocked) return;

      state.stakesMap[category] = betslipInitialStakesMap[category];
    });
    updateLastActivity(set, get);
  },

  setName(name) {
    set((state) => {
      state.name = name;
    });
    updateLastActivity(set, get);
  },

  setBoost(category, boost) {
    set((state) => {
      if (state.isLocked) return;

      state.boostMap[category] = boost;
    });
    updateLastActivity(set, get);
  },

  deleteBoost(category) {
    set((state) => {
      if (state.isLocked) return;

      delete state.boostMap[category];
    });
    updateLastActivity(set, get);
  },

  addSelection({ selection, event }) {
    set((state) => {
      if (state.isLocked) return;

      const alreadyAdded = state.selectionMap[selection.id];
      if (alreadyAdded || hasMaxSelectionsCount(state)) return;

      if (!state.rules.allowSameEventSelections) {
        deleteEventSelections(state, event.id);
      }

      addSelection(state, selection, event);
      onSelectionAdd(state, selection);
      // A changed slip is a new attempt: the last refusal no longer applies.
      state.placementError = null;
    });
    updateLastActivity(set, get);
  },

  deleteSelection(id) {
    set((state) => {
      if (state.isLocked) return;

      const selection = state.selectionMap[id];
      if (selection) {
        deleteSelection(state, id);
        onSelectionDelete(state, selection);
        state.placementError = null;
      }
    });
    updateLastActivity(set, get);
  },

  deleteSelections(ids) {
    set((state) => {
      if (state.isLocked) return;

      for (const id of ids) {
        const selection = state.selectionMap[id];
        if (selection) {
          deleteSelection(state, id);
          onSelectionDelete(state, selection);
        }
      }
      state.placementError = null;
    });
    updateLastActivity(set, get);
  },

  updateSelection({ selection, event }) {
    set((state) => {
      if (state.isLocked) return;

      const sel = state.selectionMap[selection.id];
      if (sel) {
        Object.keys(selection).forEach((key) => {
          sel[key] = selection[key];
        });
        // "Oran değişikliği otomatik kabul" (rules.oddsChangeAllowed): the
        // odds the bettor accepted follow the market, so a synced change never
        // becomes a pending "accept the change" state — no notice, no
        // "Kabul Et ve Bahis Yap" label (both read originalOddsMap through the
        // tracker's hasOddChange). With the rule off the original odds stay
        // put and the change is surfaced for the bettor to accept.
        // (Reported 2026-09-22: the setting was on, yet every odds move
        // re-asked for acceptance.)
        if (state.rules.oddsChangeAllowed && typeof sel.odds === "number") {
          state.originalOddsMap[selection.id] = sel.odds;
        }
      }

      if (event && state.eventsMap[event.id]) {
        state.eventsMap[event.id] = event;
      }
    });
    //updateLastActivity(set, get);
  },

  deleteEventSelections(eventId) {
    set((state) => {
      if (state.isLocked) return;

      deleteEventSelections(state, eventId);
      state.placementError = null;
    });
    updateLastActivity(set, get);
  },

  setRules(rules) {
    set((state) => {
      state.rules = {
        ...state.rules,
        ...rules,
      };
    });
    updateLastActivity(set, get);
  },

  setSettingsMap(settingsMap) {
    set((state) => {
      state.settingsMap = {
        ...state.settingsMap,
        ...settingsMap,
      };
    });
    updateLastActivity(set, get);
  },

  clear() {
    set((state) => {
      if (state.isLocked) return;
      return { ...betslipInitState, lastActivityTime: null };
    });
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
  },

  setError(error) {
    set((state) => {
      state.error = error;
    });
    updateLastActivity(set, get);
  },

  setPlacementError(error) {
    set((state) => {
      state.placementError = error;
    });
  },
}));

const withPersist = persist(withImmer, {
  name: storageName,
  partialize: (state) => ({
    ownerUserId: state.ownerUserId,
    events: state.events,
    category: state.category,
    selectionMap: state.selectionMap,
    selectionsByEvent: state.selectionsByEvent,
    selectionsByMarket: state.selectionsByMarket,
    eventsMap: state.eventsMap,
    selections: state.selections,
    originalOddsMap: state.originalOddsMap,
    name: state.name,
    error: state.error,
    lastActivityTime: state.lastActivityTime,
  }),
});

const useBetslipStore = create<BetslipStore>()(withPersist);

// Force-reset the slip and wipe its persisted copy, IGNORING the `isLocked`
// guard (the `clear` action respects it; on logout we must drop the slip
// unconditionally). Also removes the localStorage entry so the full page
// reload used by the remote/cross-tab logout path does not rehydrate it.
// Side effect isolated here (Rule 11); callers just invoke it.
const resetBetslipForLogout = (): void => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  useBetslipStore.setState({ ...betslipInitState, lastActivityTime: null });
  void useBetslipStore.persist.clearStorage();
};

// Stamp the current user as the owner of this slip. Called after a successful
// ownership claim so subsequent selections belong to them and the guard does
// not re-trigger for the same user.
const setBetslipOwner = (userId: number): void => {
  useBetslipStore.setState({ ownerUserId: userId });
};

export { resetBetslipForLogout, setBetslipOwner };

// İlk yüklemede kontrol
if (typeof window !== "undefined") {
  const state = useBetslipStore.getState();

  // Eğer kupon varsa ve son aktivite zamanı varsa kontrol et
  if (state.selections.length > 0 && state.lastActivityTime) {
    checkAndClearIfExpired(state.lastActivityTime, () => {
      useBetslipStore.getState().clear();
    });

    // Eğer süresi dolmamışsa, kalan süre için timer başlat
    const now = Date.now();
    const timeSinceLastActivity = now - state.lastActivityTime;
    const remainingTime = INACTIVITY_TIMEOUT - timeSinceLastActivity;

    if (remainingTime > 0) {
      inactivityTimer = setTimeout(() => {
        useBetslipStore.getState().clear();
      }, remainingTime);
    }
  }
}

// Cross-tab sync. Zustand's persist writes the slip to localStorage but does NOT
// notify other tabs, so a coupon changed in one tab (or another theme) would only
// appear in others after a reload — and a tab's periodic auto-save could clobber
// another tab's edit (e.g. a removed selection reappearing). Listen for the cross-tab
// `storage` event and rehydrate, so every tab converges on the same slip live, in
// both directions. The `storage` event fires ONLY in other tabs and ONLY when the
// value actually changed, so rehydrating to that same value writes nothing new and
// cannot loop between tabs.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === storageName) {
      void useBetslipStore.persist.rehydrate();
    }
  });
}

export { useBetslipStore };
